import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import { OrderListResponse } from "../Helpers/Interfaces/OrderType";



export const OrderService = createApi({
  reducerPath: "Orders",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ''),
  tagTypes: ["Orders"],
  endpoints: (builder) => ({
    getOrderList: builder.mutation<OrderListResponse, any>({
      query: (reqBody) => ({
        url: `${ApiPath.getOrdersList}`,
        method: "POST",
        body: reqBody
      }),
    }),
    getOrderDetails: builder.query<OrderListResponse, string>({
      query: (id) => ({
        url: `${ApiPath.getOrderDetails}/${id}`,
        method: "GET",
      }),
      providesTags: ["Orders"],
    }),
    /**
     * Third-party API calls made while fulfilling an order -- FlixBus and the
     * pin suppliers. This is where a failed supplier call and its reason can
     * be seen without going to the database.
     */
    getOrderApiLogs: builder.query<any, string>({
      query: (orderNumber) => ({
        url: `${ApiPath.getRabbitMQLogs}${orderNumber}/modal`,
        method: "GET",
      }),
    }),

    getOrderDetailsByCustomerId: builder.query<OrderListResponse, string>({
      query: (orderId) => ({
        url: `${ApiPath.getOrderDetailByCustomerId}/${orderId}`,
        method: "GET",
      })
    }),
        getCustomerOrderPin: builder.query<any, any>({
      query: (id) => ({
        url: `${ApiPath.getCustomerOrderPin}/${id}`,
        method: "GET",
      }),
    }),


    downloadInvoice: builder.query<Blob, { orderNumber: string; lang: "en" | "de" }>({
      query: ({ orderNumber, lang }) => ({
        url: `${ApiPath.invoiceDownload}/${orderNumber}/${lang}`,
        method: "GET",
        responseHandler: (response: any) => response.blob(),
      }),
    }),

    downloadCreditMemo: builder.query<Blob, { creditMemoNumber: string; lang: "en" | "de" }>({
      query: ({ creditMemoNumber, lang }) => ({
        url: `${ApiPath.creditMemoDownload}/${creditMemoNumber}/${lang}`,
        method: "GET",
        responseHandler: (response: any) => response.blob(),
      }),
    }),

    downloadRefundInvoice: builder.query<Blob, { refundInvoiceNumber: string; lang: "en" | "de" }>({
      query: ({ refundInvoiceNumber, lang }) => ({
        url: `${ApiPath.refundInvoiceDownload}/${refundInvoiceNumber}/${lang}`,
        method: "GET",
        responseHandler: (response: any) => response.blob(),
      }),
    }),

    getOrderRefundInvoices: builder.query<any, string>({
      query: (orderNumber) => ({
        url: `${ApiPath.orderRefundInvoices}/${orderNumber}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response.data,
    }),

    exportOrders: builder.mutation<Blob, {search:string, filters: { key: string; value: string | boolean }[] }>({
      async queryFn(args, _queryApi, _extraOptions, fetchWithBQ) {
        const { search, filters } = args;
        const queryParts: string[] = [];
        queryParts.push(`search=${encodeURIComponent(search.trim())}`);
        queryParts.push(`format=excel`);
        // queryParts.push(`filters[0][key]=${filters[0].key}`);
        // queryParts.push(`filters[0][value]=${filters[0].value}`);

        filters.forEach((filter, index) => {
          queryParts.push(`filters[${index}][key]=${filter.key}`);
          queryParts.push(`filters[${index}][value]=${filter.value}`);
        });

        const exportUrl = `${ApiPath.ordersExport}?${queryParts.join("&")}`;
        const result = await fetchWithBQ({
          url: exportUrl,
          method: "GET",
          responseHandler: (response: { blob: () => any; }) => response.blob(),
        });

        if (result.error) return { error: result.error };
        return { data: result.data as Blob };
      },
    }),
  }),
});

export const {
  useGetOrderListMutation,
  useGetOrderDetailsQuery,
  useLazyGetOrderApiLogsQuery,
  useLazyGetOrderDetailsByCustomerIdQuery,
  useExportOrdersMutation,
  useLazyGetCustomerOrderPinQuery,
  useLazyDownloadInvoiceQuery,
  useLazyDownloadCreditMemoQuery,
  useLazyDownloadRefundInvoiceQuery,
  useGetOrderRefundInvoicesQuery,
} = OrderService;